import type { ColorButtonKey } from './types';

export type { ColorButtonKey };

/**
 * Coffice Admin Brand Colors
 *
 * Color usage guidelines:
 * - Black: Primary CTAs (Save, Submit, Create)
 * - White/Light: Secondary actions (Cancel, Close)
 * - Grey (secondary): Neutral actions (View, Edit in tables)
 * - Red (danger): Destructive actions (Delete)
 * - Blue: Admin-specific accent (links, selections, focus states)
 * - Orange/Yellow/Green: Badges, alerts, status indicators ONLY (not buttons)
 */
export const brandColors = {
  black: '#000000',
  white: '#FFFFFF',
  orange: '#EC714B',
  yellow: '#EFE160',
  green: '#9EEB9F',
  blue: '#3B82F6', // Admin-specific accent
} as const;

// Grey Scale
export const greyScale = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
} as const;

// Avatar colors for user initials (rotation based on ID)
export const avatarColors = [
  { bg: 'bg-coffice-orange', text: 'text-white' },
  { bg: 'bg-coffice-yellow', text: 'text-grey-900' },
  { bg: 'bg-coffice-green', text: 'text-grey-900' },
  { bg: 'bg-coffice-blue', text: 'text-white' },
] as const;

export const getAvatarColor = (id?: number | string) => {
  const numericId = typeof id === 'string' ? id.charCodeAt(0) : (id ?? 0);
  return avatarColors[numericId % avatarColors.length];
};

// Theme colors aligned with brand
export const lightThemeColors = {
  primary: {
    50: '#F5F5F5',
    100: '#E5E5E5',
    500: '#000000',
    600: '#171717',
    700: '#262626',
  },
  secondary: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    500: '#737373',
    600: '#525252',
    700: '#404040',
  },
  neutral: {
    25: '#FEFEFE',
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    800: '#262626',
  },
  success: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    500: '#9EEB9F',
    600: '#7DD87E',
    700: '#5CC55D',
  },
  danger: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
  },
  warning: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    500: '#EFE160',
    600: '#D9CB56',
    700: '#C3B54C',
  },
  info: {
    50: '#FFF7ED',
    100: '#FFEDD5',
    500: '#EC714B',
    600: '#D66442',
    700: '#C05739',
  },
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
  },
};

// Background colors for components (badges, alerts, etc.)
export const colorsBgLight = {
  white: 'bg-white text-grey-900',
  light: 'bg-white text-grey-900',
  contrast: 'bg-grey-800 text-white',
  success: 'bg-coffice-green/20 text-grey-900',
  danger: 'bg-red-100 text-red-800',
  warning: 'bg-coffice-yellow/30 text-grey-900',
  info: 'bg-coffice-orange/20 text-coffice-orange',
  blue: 'bg-coffice-blue/10 text-coffice-blue',
};

// Text colors
export const colorsText = {
  white: 'text-grey-900',
  light: 'text-grey-700',
  contrast: 'text-grey-800',
  success: 'text-coffice-green',
  danger: 'text-red-600',
  warning: 'text-coffice-yellow',
  info: 'text-coffice-orange',
  blue: 'text-coffice-blue',
};

// Outline colors
export const colorsOutline = {
  white: [colorsText.white, 'border-grey-100'].join(' '),
  light: [colorsText.light, 'border-grey-100'].join(' '),
  contrast: [colorsText.contrast, 'border-grey-900'].join(' '),
  success: [colorsText.success, 'border-coffice-green'].join(' '),
  danger: [colorsText.danger, 'border-red-500'].join(' '),
  warning: [colorsText.warning, 'border-coffice-yellow'].join(' '),
  info: [colorsText.info, 'border-coffice-orange'].join(' '),
  blue: [colorsText.blue, 'border-coffice-blue'].join(' '),
};

/**
 * Button color generator
 *
 * Usage:
 * - primary: Main CTAs (Save, Submit, Create) - Black
 * - secondary: Neutral actions (View, Edit) - Grey, subtle
 * - danger: Destructive (Delete) - Red
 * - light: Cancel, Close - Very subtle
 * - info/warning: AVOID for buttons, use for badges only
 */
export const getButtonColor = (
  color: ColorButtonKey,
  isOutlined: boolean,
  hasHover: boolean,
  isActive = false,
): string => {
  if (color === 'void') {
    return '';
  }

  const colors = {
    ring: {
      primary: 'ring-grey-300',
      secondary: 'ring-grey-300',
      white: 'ring-grey-200',
      whiteDark: 'ring-grey-200',
      lightDark: 'ring-grey-200',
      contrast: 'ring-grey-300',
      success: 'ring-coffice-green/50',
      danger: 'ring-red-300',
      warning: 'ring-coffice-yellow/50',
      info: 'ring-coffice-orange/50',
      light: 'ring-grey-200',
    },
    active: {
      primary: 'bg-grey-700',
      secondary: 'bg-grey-300',
      white: 'bg-grey-100',
      whiteDark: 'bg-grey-100',
      lightDark: 'bg-grey-200',
      contrast: 'bg-grey-700',
      success: 'bg-coffice-green/80',
      danger: 'bg-red-700',
      warning: 'bg-coffice-yellow/80',
      info: 'bg-coffice-orange/80',
      light: 'bg-grey-100',
    },
    bg: {
      primary: 'bg-coffice-black text-white',
      secondary: 'bg-grey-100 text-grey-700',
      white: 'bg-white text-grey-900',
      whiteDark: 'bg-white text-grey-900',
      lightDark: 'bg-grey-100 text-grey-900',
      contrast: 'bg-grey-800 text-white',
      success: 'bg-coffice-green text-grey-900',
      danger: 'bg-red-500 text-white',
      warning: 'bg-coffice-yellow text-grey-900',
      info: 'bg-coffice-orange text-white',
      light: 'bg-grey-50 text-grey-700',
    },
    bgHover: {
      primary: 'hover:bg-grey-800 hover:border-grey-800',
      secondary: 'hover:bg-grey-200 hover:border-grey-200',
      white: 'hover:bg-grey-50',
      whiteDark: 'hover:bg-grey-100',
      lightDark: 'hover:bg-grey-200',
      contrast: 'hover:bg-grey-700',
      success: 'hover:bg-coffice-green/80 hover:border-coffice-green/80',
      danger: 'hover:bg-red-600 hover:border-red-600',
      warning: 'hover:bg-coffice-yellow/80 hover:border-coffice-yellow/80',
      info: 'hover:bg-coffice-orange/80 hover:border-coffice-orange/80',
      light: 'hover:bg-grey-100',
    },
    borders: {
      primary: 'border-coffice-black',
      secondary: 'border-grey-200',
      white: 'border-white',
      whiteDark: 'border-white',
      lightDark: 'border-grey-100',
      contrast: 'border-grey-800',
      success: 'border-coffice-green',
      danger: 'border-red-500',
      warning: 'border-coffice-yellow',
      info: 'border-coffice-orange',
      light: 'border-grey-200',
    },
    text: {
      primary: 'text-coffice-black',
      secondary: 'text-grey-600',
      white: 'text-grey-900',
      whiteDark: 'text-grey-900',
      lightDark: 'text-grey-700',
      contrast: 'text-grey-800',
      success: 'text-coffice-green',
      danger: 'text-red-600',
      warning: 'text-coffice-yellow',
      info: 'text-coffice-orange',
      light: 'text-grey-600',
    },
    outlineHover: {
      primary:
        'hover:bg-coffice-black hover:text-white hover:border-coffice-black',
      secondary: 'hover:bg-grey-100 hover:text-grey-700 hover:border-grey-300',
      white: 'hover:bg-grey-50 hover:text-grey-900',
      whiteDark: 'hover:bg-grey-100 hover:text-grey-900',
      lightDark: 'hover:bg-grey-200 hover:text-grey-900',
      contrast: 'hover:bg-grey-800 hover:text-grey-100',
      success:
        'hover:bg-coffice-green hover:text-grey-900 hover:border-coffice-green',
      danger: 'hover:bg-red-500 hover:text-white hover:border-red-500',
      warning:
        'hover:bg-coffice-yellow hover:text-grey-900 hover:border-coffice-yellow',
      info: 'hover:bg-coffice-orange hover:text-white hover:border-coffice-orange',
      light: 'hover:bg-grey-100 hover:text-grey-900',
    },
  };

  const isOutlinedProcessed =
    isOutlined &&
    !['white', 'whiteDark', 'lightDark', 'light', 'secondary'].includes(color);

  const base = [colors.borders[color], colors.ring[color]];

  if (isActive) {
    base.push(colors.active[color]);
  } else {
    base.push(isOutlinedProcessed ? colors.text[color] : colors.bg[color]);
  }

  if (hasHover) {
    base.push(
      isOutlinedProcessed ? colors.outlineHover[color] : colors.bgHover[color],
    );
  }

  return base.join(' ');
};
